/**
 * Simulation Service
 * API client for simulation management with authentication.
 */
import { api } from './authService';

const simulationService = {
  /**
   * Check if current user can launch simulations
   * Returns { can_launch: boolean, reason: string | null }
   */
  canLaunchSimulations: async () => {
    try {
      const response = await api.get('/api/simulations/can-launch/');
      return response.data;
    } catch (error) {
      // If error, assume can't launch
      return {
        can_launch: false,
        reason: error.response?.data?.message || 'Failed to check permissions'
      };
    }
  },

  /**
   * Get dashboard statistics
   */
  getDashboardStats: async () => {
    const response = await api.get('/api/simulations/dashboard/');
    return response.data;
  },

  /**
   * List all simulations with optional filters
   */
  listSimulations: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.workflow) params.append('workflow', filters.workflow);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await api.get(`/api/simulations/?${params.toString()}`);
    return response.data;
  },

  /**
   * Get running simulations
   */
  getRunningSimulations: async () => {
    const response = await api.get('/api/simulations/running/');
    return response.data;
  },

  /**
   * Create a new simulation
   */
  createSimulation: async (data) => {
    const response = await api.post('/api/simulations/create/', data);
    return response.data;
  },

  /**
   * Get simulation details
   */
  getSimulation: async (simulationId) => {
    const response = await api.get(`/api/simulations/detail/${simulationId}/`);
    return response.data;
  },

  /**
   * Get the timeline (step-by-step progress) for a simulation.
   * Used by the "build console" UI on the detail page.
   */
  getTimeline: async (simulationId) => {
    const response = await api.get(`/api/simulations/detail/${simulationId}/timeline/`);
    return response.data;
  },

  /**
   * Delete a simulation
   */
  deleteSimulation: async (simulationId) => {
    const response = await api.delete(`/api/simulations/detail/${simulationId}/delete/`);
    return response.data;
  },

  /**
   * Terminate a running simulation
   */
  terminateSimulation: async (simulationId, reason = 'User requested termination') => {
    const response = await api.post(`/api/simulations/terminate/${simulationId}`, { reason });
    return response.data;
  },

  /**
   * Get simulation results (legacy endpoint)
   */
  getResults: async (simulationId) => {
    const response = await api.get(`/api/simulations/results/${simulationId}`);
    return response.data;
  },

  /**
   * Get the catalog of manifest-driven cases.
   *
   * Source of truth: MyMDO-DAFoam/cases/<Name>/mymdo.json, aggregated by the
   * DAFoam pipeline and served by GET /api/simulations/cases/. Adding a case
   * there makes it appear here — no frontend change, no redeploy.
   *
   * Returns { source, generated_at, cases: [...], templates: [...] }.
   */
  getCaseCatalog: async () => {
    const response = await api.get('/api/simulations/cases/');
    return response.data;
  },

  /**
   * Get simulation status (legacy endpoint)
   */
  getStatus: async (simulationId) => {
    const response = await api.get(`/api/simulations/status/${simulationId}`);
    return response.data;
  },

  // Display names for the legacy workflows. Catalog cases carry their own
  // label in the manifest; pass the catalog cases as the second argument to
  // resolve them (see useCaseCatalog).
  getWorkflowDisplayName: (workflow, cases = null) => {
    if (cases) {
      const found = cases.find((c) => c.id === workflow);
      if (found) return found.label;
    }
    const names = {
      drone_wing: 'Drone Wing Analysis',
      mach_wing: 'MACH Wing Optimization',
      dlr: 'DLR F6 CFD Analysis',
      custom_geometry: 'Custom Geometry CFD',
    };
    return names[workflow] || workflow;
  },

  getStatusColor: (status) => {
    const colors = {
      queued: '#f59e0b',
      running: '#3b82f6',
      completed: '#10b981',
      failed: '#ef4444',
    };
    return colors[status] || '#6b7280';
  },

  /**
   * Vue d'ensemble des diagnostics de calcul — superadmin uniquement.
   * Le serveur renvoie 403 à quiconque d'autre : cet appel n'est pas un garde.
   */
  getDiagnostics: async (limite = 50) => {
    const response = await api.get(`/api/simulations/diagnostics/?limite=${limite}`);
    return response.data;
  },

  /**
   * Calculs bloqués faute de machine Spot — superadmin uniquement.
   * Le serveur renvoie 403 à quiconque d'autre : cet appel n'est pas un garde.
   */
  getPendingSimulations: async () => {
    const response = await api.get('/api/simulations/admin/en-attente/');
    return response.data;
  },

  /**
   * Relance à la demande d'un calcul bloqué. `montantUsd` est le montant
   * affiché à l'admin : le serveur le recalcule et refuse s'il a changé.
   */
  relaunchOnDemand: async (simulationId, montantUsd) => {
    const response = await api.post(
      `/api/simulations/admin/${encodeURIComponent(simulationId)}/relancer-a-la-demande/`,
      { confirmation_cout_usd: montantUsd }
    );
    return response.data;
  },

  /**
   * Dépose un fichier utilisateur et renvoie sa clé S3.
   *
   * Le fichier ne transite JAMAIS par le backend : celui-ci signe une
   * autorisation, le navigateur envoie directement à S3. Pas de limite de
   * taille imposée par Lambda, pas de transfert payé deux fois.
   *
   * Les formats acceptés et la taille maximale viennent du manifeste du case,
   * côté serveur. Ce qui est vérifié ici ne l'est que pour éviter un aller-retour
   * inutile — le serveur reste seul juge.
   */
  envoyerFichier: async (fichier, { workflowMode, inputName, onProgres } = {}) => {
    const { data } = await api.post('/api/simulations/upload-url', {
      filename: fichier.name,
      workflow_mode: workflowMode,
      input_name: inputName,
    });

    await new Promise((resolve, reject) => {
      const formData = new FormData();
      // L'ordre compte pour S3 : les champs signés d'abord, le fichier en dernier.
      Object.entries(data.upload_fields || {}).forEach(([cle, valeur]) => {
        formData.append(cle, valeur);
      });
      formData.append('file', fichier);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', data.upload_url, true);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgres) {
          onProgres(Math.round((100 * e.loaded) / e.total));
        }
      };
      xhr.onload = () => (
        xhr.status === 204 || xhr.status === 200
          ? resolve()
          : reject(new Error(`upload ${xhr.status}`))
      );
      xhr.onerror = () => reject(new Error('upload failed'));
      xhr.send(formData);
    });

    return data.s3_key;
  },

  // Deprecated: Use StatusIcon component for SVG icons
  getStatusIcon: () => null,

  formatDuration: (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  },
};

export default simulationService;
